import React, { SyntheticEvent, useEffect, useState } from "react";
import { Button, Card, Icon, Popup } from "semantic-ui-react";
import LuxonDataTable from "../../components/LuxonDataTable";
import { hoursPassedSince, formatDateTime } from "../../utils/datetimeUtils";
import InputSearch from "../../components/InputSearch";
import PageHeader from "../../components/PageHeader";
import { cancelAppointmentById, getProspectsAppointments, markProspectQuotedById, markProspectVisitedById } from "../../services/prospects.service";
import ProspectVisitedModal from "./modals/ProspectVisitedModal";
import QuoteSentModal from "./modals/QuoteSentModal";
import CancelAppointmentModal from "./modals/CancelAppointmentModal";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/AuthProvider";
import RootTemplate from "../../components/RootTemplate";
import ProspectAppointment from "../../types/entities/ProspectAppointment";
import CreateNewProspectModal from "./modals/CreateNewProspect/CreateNewProspectModal";
import UpdateProspectModal from "./modals/CreateNewProspect/UpdatedProspectModal";
import FollowUpAppointmentModal from "./modals/CreateNewProspect/FollowUpAppointmentModal";

const AssignmentsList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [search, setSearch] = useState("");

    const auth = useAuth();

    // State for Update Modal
    const [selectedProspectId, setSelectedProspectId] = useState<string>("");
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);

    const handleEdit = (id: string) => {
        setSelectedProspectId(id);
        setIsUpdateModalOpen(true);
    };

    const closeUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedProspectId("");
    };

    const handleFollowUp = (id: string) => {
        setSelectedProspectId(id);
        setIsFollowUpModalOpen(true);
    };

    const closeFollowUpModal = () => {
        setIsFollowUpModalOpen(false);
        setSelectedProspectId("");
    };

    const columns = [
        {
            name: "Fecha de visita",
            selector: (row: ProspectAppointment) => (
                <>
                    {`${formatDateTime(row.appointment.dateTime)} `}
                    {row.type === "SHOWROOM" && (
                        <Popup
                            trigger={
                                <Icon circular name="home" />
                            }
                            content='Showroom Luxon'
                            position='top center'
                            size='small'
                            inverted
                        />
                    )}
                </>
            ),
        },
        {
            name: "Nombre del prospecto",
            selector: (row: ProspectAppointment) => row.prospect.person?.fullName ?? "",
        },
        {
            name: "Producto",
            selector: (row: ProspectAppointment) => row.prospect.product.name,
        },
        {
            name: "Asignado por",
            selector: (row: ProspectAppointment) => row.prospect.createdBy?.person?.fullName || '',
        },
        {
            name: "Acciones",
            button: true,
            cell: (row: ProspectAppointment) => (
                <>
                    {row.status === "VISITED" ? (
                        <>
                            <QuoteSentModal prospectAppointmentId={row.id} onSubmit={handleQuoted} />
                            <Popup
                                trigger={
                                    <Button color="blue" icon size="mini" onClick={() => handleFollowUp(row.id)}>
                                        <Icon name="calendar plus outline" />
                                    </Button>
                                }
                                content='Agendar cita de seguimiento'
                                position='top center'
                                size='small'
                                inverted
                            />
                        </>
                    ) : (
                        <>
                            <CancelAppointmentModal prospectAppointmentId={row.id} onSubmit={handleCancelationAppointment} />
                            <ProspectVisitedModal
                                prospectAppointmentId={row.id}
                                prospectAppointmentType={row.type}
                                onSubmit={handleVisited}
                                disabled={false}
                                defaultLatitude={row.appointment?.address?.latitude}
                                defaultLongitude={row.appointment?.address?.longitude}
                            />
                            {row.prospect.createdBy.id === auth.userData?.id && (
                                <Popup
                                    trigger={
                                        <Button color="yellow" icon size="mini" onClick={() => handleEdit(row.id)}>
                                            <Icon name="pencil" />
                                        </Button>
                                    }
                                    content='Editar cita'
                                    position='top center'
                                    size='small'
                                    inverted
                                />
                            )}

                        </>
                    )}
                </>
            ),
        },
    ];

    const conditionalRowStyles = [
        {
            when: (row: ProspectAppointment) => (row.status === "VISITED") && row.visitedAt && (hoursPassedSince(row.visitedAt) > 24),
            style: {
                backgroundColor: "#ffeaeaff",
                "&:hover": {
                    cursor: "pointer",
                },
            },
        },
    ];

    const fetchData = async (page: number, search?: string) => {
        setLoading(true);
        try {
            const response = await getProspectsAppointments(perPage, page - 1, "createdAt,desc", search, "SCHEDULED,VISITED");
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
        } catch (error) {
            toast.error("Error al obtener la informacion de las asignaciones");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        fetchData(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        try {
            const response = await getProspectsAppointments(newPerPage, page - 1, "createdAt,desc", search, "SCHEDULED,VISITED");
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
            setPerPage(newPerPage);
        } catch (error) {
            toast.error("Error al obtener la informacion de las asignaciones");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1, search);
        // eslint-disable-next-line
    }, [, search]);

    const handleCancelationAppointment = async (event: SyntheticEvent, prospectAppointmentId: string | undefined, cancellationCause: string) => {
        event.preventDefault();
        setLoading(true);
        try {
            if (!prospectAppointmentId) {
                throw new Error("No se proporciono un prospecto para cancelar la cita.");
            }
            const response = await cancelAppointmentById(prospectAppointmentId, cancellationCause);
            if (response?.status === 200) {
                fetchData(1, search);
                toast.success("La cita fue cancelada con exito.");
            } else {
                throw new Error("Ha ocurrido un error al cancelar la cita para este prospecto.");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleVisited = async (
        event: SyntheticEvent,
        prospectAppointmentId: string | undefined,
        latitude?: number,
        longitude?: number
    ) => {
        event.preventDefault();
        setLoading(true);
        try {
            if (!prospectAppointmentId) {
                throw new Error("No se proporciono un prospecto para marcar como visitado.");
            }
            const response = await markProspectVisitedById(prospectAppointmentId, latitude, longitude);
            if (response?.status === 200) {
                fetchData(1, search);
                toast.success("El prospecto fue marcado como visitado exitosamente.");
            } else {
                throw new Error("Ha ocurrido un error al marcar como visitado el prospecto.");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleQuoted = async (event: SyntheticEvent, prospectAppointmentId: string | undefined) => {
        event.preventDefault();
        setLoading(true);
        try {
            if (!prospectAppointmentId) {
                throw new Error("Error al obtener el ID de la cita para el prospecto seleccionado.");
            }
            const response = await markProspectQuotedById(prospectAppointmentId);
            if (response?.status === 200) {
                fetchData(1, search);
                toast.success("El prospecto ha sido cotizado exitosamente.");
            } else {
                throw new Error("Ha ocurrido un error al marcar como cotizado el prospecto.");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitProspect = () => {
        fetchData(1);
    }

    const handleUpdateProspect = () => {
        // Just refresh the data, the modal will be closed by the UpdatedProspectModal calling onClose from props,
        // but we should technically wait for the data refresh.
        // The modal calls onProspectUpdated then onClose. 
        fetchData(1, search); // Refresh current view
    }

    return (
        <RootTemplate>

            <PageHeader
                title="Asignaciones"
                subtitle="Clientes potenciales"
                actionComponents={
                    <CreateNewProspectModal onProspectCreated={handleSubmitProspect} />
                }
            />

            <InputSearch
                icon="search"
                placeholder="Búsqueda por nombre de prospecto..."
                setSearch={setSearch}
            />

            <Card fluid>
                <LuxonDataTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    totalRows={totalRows}
                    conditionalRowStyles={conditionalRowStyles}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                />
            </Card>

            <UpdateProspectModal
                isOpen={isUpdateModalOpen}
                onClose={closeUpdateModal}
                prospectAppointmentId={selectedProspectId}
                onProspectUpdated={handleUpdateProspect}
            />

            <FollowUpAppointmentModal
                isOpen={isFollowUpModalOpen}
                onClose={closeFollowUpModal}
                prospectAppointmentId={selectedProspectId}
                onProspectUpdated={handleUpdateProspect}
            />

        </RootTemplate>
    );
};

export default AssignmentsList;
